import { call } from './apiHelpers';
import { apiClient } from './apiClient';

const REPORT_PATHS = Object.freeze({
  VOTERS_BY_COORDINATOR: 'voters-by-coordinator',
  VOTERS_BY_LEADER: 'voters-by-leader',
  VOTERS_BY_CLASSIFICATION: 'voters-by-classification',
  VOTERS_BY_STATUS: 'voters-by-status',
  SHEETS_BY_STATUS: 'sheets-by-status',
  CALL_RESULTS: 'calls-results',
  SHEET_RESPONSE: 'sheets-response-rate',
  DRIVERS_BY_TYPE: 'drivers-by-type',
  VEHICLES_BY_COORDINATOR: 'vehicles-by-coordinator',
  VEHICLE_OCCUPANCY: 'vehicle-occupancy',
  VOTERS_NEEDING_VEHICLE: 'voters-pending-vehicle',
  DUPLICATES: 'duplicates',
  DAILY_RECORDS: 'daily-registrations',
  VOTERS_BY_SECTIONAL: 'voters-by-sectional',
  COORDINATORS_BY_SECTIONAL: 'coordinators-by-sectional',
  LEADERS_BY_SECTIONAL: 'leaders-by-sectional',
});

const prettify = (key) => key.replaceAll('_', ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const adaptReport = (payload, type) => {
  const aggregateRows = payload.results || [];
  const rows = payload.details || aggregateRows;
  const keys = [...new Set(rows.flatMap((row) => Object.keys(row)))];
  const aggregate = keys.includes('label') && keys.includes('value')
    && keys.every((key) => ['id', 'label', 'value'].includes(key));
  const visibleKeys = aggregate
    ? ['label', 'value']
    : keys.filter((key) => key !== 'id');
  const columns = visibleKeys.map((key) => ({
    key,
    label: key === 'label' ? 'Categoría' : key === 'value' ? 'Total' : prettify(key),
  }));
  return {
    ...payload,
    type,
    total: payload.totals?.total ?? rows.length,
    generatedAt: new Date().toISOString(),
    rows,
    columns: columns.length ? columns : [
      { key: 'label', label: 'Categoría' }, { key: 'value', label: 'Total' },
    ],
    chart: aggregateRows
      .filter((row) => row.label !== undefined && row.value !== undefined)
      .map((row) => ({ name: row.label, value: row.value })),
  };
};

const SHEET_REPORTS = new Set(['SHEETS_BY_STATUS', 'SHEET_RESPONSE', 'CALL_RESULTS']);

const apiFilters = (filters = {}, type = '') => ({
  campaign_id: filters.campaignId,
  general_administrator_id: filters.generalAdministratorId,
  coordinator_id: filters.coordinatorId,
  leader_id: filters.leaderId,
  classification: filters.classification,
  status: SHEET_REPORTS.has(type) ? filters.sheetStatus : filters.voterStatus,
  date_from: filters.fromDate,
  date_to: filters.toDate,
  zone: filters.zone,
  sectional: filters.sectional,
  search: filters.search,
});

export const reportService = {
  getOptions: () => call('get', 'reports/options/'),
  async getReport(type, _, filters) {
    const payload = await apiClient.get(`reports/${REPORT_PATHS[type]}/`, {
      params: apiFilters(filters, type),
    });
    return { data: adaptReport(payload, type) };
  },
  async exportFile(type, filters, format, filename) {
    const expectedType = format === 'pdf'
      ? 'application/pdf'
      : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const result = await apiClient.download('reports/export/', {
      accept: expectedType,
      params: {
        report: REPORT_PATHS[type],
        // Avoid DRF's reserved format query parameter.
        export_format: format,
        ...apiFilters(filters, type),
      },
    });
    const signature = new Uint8Array(await result.blob.slice(0, 4).arrayBuffer());
    const valid = format === 'pdf'
      ? String.fromCharCode(...signature) === '%PDF'
      : signature[0] === 0x50 && signature[1] === 0x4b;
    if (!valid) {
      throw new Error(`El servidor devolvió un contenido inválido para ${format.toUpperCase()}.`);
    }
    const url = URL.createObjectURL(result.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `${REPORT_PATHS[type]}.${format}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
  exportExcel(type, filters) {
    return this.exportFile(type, filters, 'xlsx', `${REPORT_PATHS[type]}.xlsx`);
  },
  exportPdf(type, filters, title) {
    return this.exportFile(type, filters, 'pdf', `${title || REPORT_PATHS[type]}.pdf`);
  },
};
